import { Injectable, DestroyRef, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  EventType,
  InteractionStatus,
  type AccountInfo,
  type AuthenticationResult,
  type SilentRequest,
} from '@azure/msal-browser';
import { filter, take, map, catchError, of, type Observable } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { APP_PUBLIC_CONFIG } from '#core';
import { IdpSource } from '#enums';
import { type RoleAssignment, type UserDetails } from '#models';
import { environment } from '#environments';
import { GraphService } from './graph.service';
import { LoggingService } from './logging.service';
import { OktaAuthService } from './okta-auth.service';
import { UserSessionService } from './user-session.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _msalService = inject(MsalService);
  private _msalBroadcastService = inject(MsalBroadcastService);
  private _destroyRef = inject(DestroyRef);
  private _config = inject(APP_PUBLIC_CONFIG);
  private _oktaService = inject(OktaAuthService);

  private _http = inject(HttpClient);
  private _router = inject(Router);
  private _graphService = inject(GraphService);
  private _sessionService = inject(UserSessionService);
  private _log = inject(LoggingService);
  private _activeAccount = signal<AccountInfo | null>(null);
  private _userDetails = signal<UserDetails | null>(null);
  private _profilePhoto = signal<string | null>(null);

  readonly activeAccount = this._activeAccount.asReadonly();
  readonly isAuthenticated = computed(() => this._activeAccount() !== null || this._oktaService.isAuthenticated());
  readonly displayName = computed(() => this._activeAccount()?.name ?? this._oktaService.displayName());
  readonly email = computed(() => this._activeAccount()?.username ?? this._oktaService.email());
  readonly userDetails = this._userDetails.asReadonly();
  /** Microsoft Graph profile photo as a data URL, or null when unavailable. */
  readonly profilePhoto = this._profilePhoto.asReadonly();

  constructor() {
    this._log.info('AuthService', 'constructor');

    // ── Canonical MSAL Angular pattern ───────────────────────────────────────
    // Wait for MSAL to finish ALL in-progress operations before reading
    // accounts or calling any API. This is the SINGLE entry point for
    // both returning users (account already cached) and fresh logins
    // (account just added by handleRedirectObservable in APP_INITIALIZER).
    // Using take(1) ensures this fires exactly once per page load.
    this._msalBroadcastService.inProgress$
      .pipe(
        filter(status => status === InteractionStatus.None),
        take(1),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => {
        const accounts = this._msalService.instance.getAllAccounts();
        this._log.info('AuthService', `MSAL settled — accounts: ${accounts.length}`);

        if (accounts.length === 0) return; // unauthenticated — authGuard will redirect

        const account = this._msalService.instance.getActiveAccount() ?? accounts[0];
        this._msalService.instance.setActiveAccount(account);
        this._activeAccount.set(account);
        this._log.info('AuthService', `account set: ${account.username}`);
        this._log.setUser(account.username, account.name ?? account.username);

        this._buildAndSaveUserProfile(account);
        this._fetchUserDetails();
        this._fetchProfilePhoto();
      });
    // ── Okta (external third-party) session restore ───────────────────────────
    // OktaAuthService restores any cached OIDC session from sessionStorage on boot,
    // but that alone never re-fetches UserDetails/scopes. Without this, a plain
    // page reload keeps serving whatever was cached at the last full Okta login,
    // even after an admin later changes the user's vendor/project assignments.
    // onOktaAuthenticated() (called from the /auth callback route) covers the
    // fresh-login case; this covers every subsequent reload of an existing session.
    toObservable(this._oktaService.isAuthenticated)
      .pipe(
        filter(isAuth => isAuth),
        take(1),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => {
        this._log.info('AuthService', 'Okta session restored on boot — refreshing user details');
        this._fetchUserDetails();
        this._fetchProfilePhoto();
      });
    // ── Logout ─────────────────────────────────────────────────────────────
    this._msalBroadcastService.msalSubject$
      .pipe(
        filter(event => event.eventType === EventType.LOGOUT_SUCCESS),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => {
        this._log.info('AuthService', 'LOGOUT_SUCCESS — clearing state');
        this._sessionService.removeCurrentUser();
        this._activeAccount.set(null);
        this._userDetails.set(null);
        this._profilePhoto.set(null);
        this._log.clearUser();
      });
  }

  /** Triggers an Entra ID redirect-based login. */
  login(): void {
    this._msalService.loginRedirect({
      scopes: [
        'openid',
        'profile',
        'email',
        this._config.apiScopeUri
      ],
    });
  }

  /** Triggers login with Chevron SSO (home-tenant, no domain hint needed). */
  loginChevron(): void {
    this._msalService.loginRedirect({
      scopes: ['openid', 'profile', 'email', this._config.apiScopeUri],
      extraQueryParameters: { domain_hint: 'chevron.com' },
    });
  }

  /** Initiates an Okta PKCE redirect for external / third-party users. */
  loginExternal(): void {
    this._oktaService.login();
  }

  /** Logs the user out of the active IdP and redirects to the login page. */
  logout(): void {
    this._log.info('AuthService', 'logout — initiated');
    this._userDetails.set(null);
    this._profilePhoto.set(null);
    this._sessionService.removeCurrentUser();
    this._log.clearUser();

    if (this._oktaService.isAuthenticated()) {
      this._oktaService.logout().then(() => this._router.navigate(['/login']));
      return;
    }

    const account = this._activeAccount();
    this._activeAccount.set(null);
    this._msalService.logoutRedirect({
      account,
      postLogoutRedirectUri: `${location.origin}/login`,
    });
  }

  /**
   * Called by AuthCallbackComponent after a successful Okta callback.
   * Writes a skeleton profile from token claims immediately, then fetches API data.
   */
  onOktaAuthenticated(): void {
    this._buildAndSaveExternalUserProfile();
    this._fetchUserDetails();
  }
  getAccessToken(): Observable<string | null> {
    const account = this._activeAccount();
    if (!account) {
      return of(null);
    }

    const silentRequest: SilentRequest = {
      account,
      scopes: [
        this._config.apiScopeUri
      ],
    };

    return this._msalService.acquireTokenSilent(silentRequest).pipe(
      map((result: AuthenticationResult) => result.accessToken),
      catchError(() => {
        // Silent acquisition failed � the error interceptor will handle the 401
        return of(null);
      }),
    );
  }

  private _fetchUserDetails(): void {
    const url = `${environment.apiBaseUrl}/v1/auth/me`;
    this._log.info('AuthService', `_fetchUserDetails → GET ${url}`);

    this._http
      .get<UserDetails>(url)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: apiDetails => {
          let details: UserDetails;
          if (this._oktaService.isAuthenticated()) {
            // Identity from oidc-client-ts (authoritative for Okta users)
            const oktaUserId = this._oktaService.userId();
            const oktaEmail = this._oktaService.email() || oktaUserId;
            const oktaDisplayName = this._oktaService.displayName()
              || this._formatEmailAsDisplayName(oktaUserId);
            details = {
              // Authorization fields (role, roles, scopes, permissions, isActive, isExternal, idpSource) from API
              ...apiDetails,
              // Identity fields always from oidc-client-ts
              userId: oktaUserId || apiDetails.userId,
              email: oktaEmail || apiDetails.email,
              displayName: oktaDisplayName || apiDetails.displayName,
            };
          } else {
            details = apiDetails;
          }

          this._log.info('AuthService', '_fetchUserDetails ✓ success', { userId: details.userId, isActive: details.isActive });
          this._userDetails.set(details);
          this._sessionService.saveCurrentUser(details);
          // Seed the active role signal from the API response (source of truth after this change).
          // Uses !== null (not a truthy check) so the empty-string "All Roles" sentinel (FB-004)
          // is captured too — only a true null ("never chosen") is skipped.
          if (details.activeRoleId !== null) {
            this._sessionService.saveActiveRoleId(details.activeRoleId);
          }
          if (details.profilePhoto) {
            this._profilePhoto.set(details.profilePhoto);
          }

          // Supplemental permissions fetch for external users when /auth/me returns empty permissions.
          // Triggered when the DB sync hasn't resolved yet on first login (e.g. AppRoleValue mismatch).
          if (this._oktaService.isAuthenticated() && details.permissions.length === 0 && details.role) {
            this._fetchRolePermissions(details);
          }

          // After a fresh login the MSAL callback lands on /auth (or /login); send the user into the app.
          // Also escape /account-pending when the API now confirms isActive:true (stale cache was used before).
          const onEntryUrl = this._router.url.startsWith('/auth') || this._router.url === '/login'
            || (details.isActive && this._router.url.startsWith('/account-pending'));
          if (onEntryUrl) {
            this._router.navigate(['/asset-inventory']);
          }
        },
        error: err => {
          this._log.error('AuthService', `_fetchUserDetails ✗ failed — status: ${err?.status}`, {
            status: err?.status,
            url: err?.url,
            message: err?.message,
            body: err?.error,
          });
        },
      });
  }

  /** Supplements /auth/me when permissions are empty — calls /auth/role-permissions and patches localStorage. */
  private _fetchRolePermissions(currentDetails: UserDetails): void {
    const url = `${environment.apiBaseUrl}/v1/auth/role-permissions`;
    this._log.info('AuthService', `_fetchRolePermissions → GET ${url} (role=${currentDetails.role})`);

    this._http
      .get<{ roleId: string; roleName: string; permissions: Record<string, string[]>; scopes: null }>(url)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: dto => {
          const roleAssignment: RoleAssignment = {
            roleId: dto.roleId,
            roleName: dto.roleName,
            permissions: dto.permissions,
            scopes: null,
          };
          // Derive the flat permissions union from the resource-keyed map.
          const flatPermissions = [...new Set(Object.values(dto.permissions).flat())];

          this._log.info('AuthService', `_fetchRolePermissions ✓ ${Object.keys(dto.permissions).length} resources, ${flatPermissions.length} actions`);

          const patched: UserDetails = {
            ...currentDetails,
            roles: [roleAssignment],
            permissions: flatPermissions,
          };
          this._userDetails.set(patched);
          this._sessionService.saveCurrentUser(patched);
        },
        error: err => {
          this._log.warn('AuthService', `_fetchRolePermissions ✗ failed — status: ${err?.status}`);
        },
      });
  }

  /** "guruprakash.c@arcadis.com" → "Guruprakash C" */
  private _formatEmailAsDisplayName(email: string): string {
    const local = email.includes('@') ? email.split('@')[0] : email;
    return local.split(/[._-]/).filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Fetches the Microsoft Graph profile photo independently of /auth/me.
   * Runs immediately after the account is known.
   */
  private _fetchProfilePhoto(): void {
    this._graphService
      .getProfilePhoto()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: photoUrl => {
          this._profilePhoto.set(photoUrl);
          // Patch profilePhoto into the stored UserDetails so the photo survives
          // a page refresh even after the Graph request resolves.
          const session = this._sessionService.getCurrentUser();
          if (session && photoUrl) {
            this._sessionService.saveCurrentUser({ ...session, profilePhoto: photoUrl });
          }
        },
        // GraphService never throws — errors are caught and mapped to null internally.
      });
  }

  /** Writes a skeleton {@link UserDetails} from Okta token claims so the UI renders before the API responds. */
  private _buildAndSaveExternalUserProfile(): void {
    const existing = this._sessionService.getCurrentUser();
    const currentUserId = this._oktaService.userId();
    // Reuse cached session only when it belongs to the SAME user — a different Okta account
    // must always start fresh so stale identity data is never shown.
    // Only reuse cache when the account was known to be active — isActive can change between sessions.
    if (existing && existing.roles?.length > 0 && existing.isActive && existing.userId === currentUserId) {
      this._userDetails.set(existing);
      return;
    }

    const userId = this._oktaService.userId();
    const email = this._oktaService.email() || userId;
    const displayName = this._oktaService.displayName()
      || this._formatEmailAsDisplayName(userId);

    const initial: UserDetails = {
      userId,
      email,
      displayName,
      role: 'ARES.ThirdParty',
      roles: [],
      isActive: false,
      isExternal: true,
      idpSource: IdpSource.Okta,
      profilePhoto: null,
      permissions: [],
      scopes: [],
      activeRoleId: null,
    };

    this._log.info('AuthService', '_buildAndSaveExternalUserProfile — skeleton saved', { userId });
    this._sessionService.saveCurrentUser(initial);
  }

  /**
   * Builds an initial {@link UserDetails} from the MSAL ID-token claims of the
   * given account and immediately persists it to localStorage so the UI can
   * display the user's name before the API call resolves.
   *
   * Skipped when the existing session already contains authoritative API data
   * (i.e. permissions are populated) — this avoids overwriting valid data on
   * every page refresh before {@link _fetchUserDetails} has a chance to respond.
   *
   * Claim priority:
   *  - displayName: name → given_name + family_name → email
   *  - email:       preferred_username → email → upn → account.username
   */
  private _buildAndSaveUserProfile(account: AccountInfo): void {
    // Reuse cached session only when it belongs to the SAME user.
    const existing = this._sessionService.getCurrentUser();
    const currentEmail: string =
      (account.idTokenClaims?.['preferred_username'] as string | undefined) ??
      account.username ?? '';
    if (existing && existing.roles?.length > 0 && existing.email === currentEmail) {
      this._userDetails.set(existing);
      return;
    }

    const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>;

    const nameClaim = claims['name'] as string | undefined;
    const givenName = claims['given_name'] as string | undefined;
    const familyName = claims['family_name'] as string | undefined;
    const preferredUsername = claims['preferred_username'] as string | undefined;
    const emailClaim = claims['email'] as string | undefined;
    const upn = claims['upn'] as string | undefined;
    const oidClaim = claims['oid'] as string | undefined;
    const subClaim = claims['sub'] as string | undefined;

    const email: string =
      preferredUsername ?? emailClaim ?? upn ?? account.username ?? '';

    const displayName: string =
      nameClaim ??
      (givenName && familyName ? `${givenName} ${familyName}` : undefined) ??
      email;

    const initial: UserDetails = {
      userId: oidClaim ?? subClaim ?? account.localAccountId ?? '',
      email,
      displayName,
      role: '',
      roles: [],
      isActive: false,
      isExternal: false,
      idpSource: IdpSource.Entra,
      profilePhoto: null,
      permissions: [],
      scopes: [],
      activeRoleId: null,
    };

    this._sessionService.saveCurrentUser(initial);
  }
}
